/**
 * Mode Adapter — Single-object facade for all mode checks.
 *
 * Instead of importing multiple functions (`isMockMode`, `isBackendEnabled`, etc.)
 * consumers import `{ mode }` and access semantic properties:
 *
 *   import { mode } from '@/lib/config/app-mode';
 *
 *   if (mode.useRealAPI) { ... }
 *   if (mode.showSampleData) { ... }
 *
 * All properties are getters so they reflect the current runtime state
 * (including _demoBypass toggling).
 */

import {
  isMockMode,
  isProductionMode,
  isBackendEnabled,
  useRealAuth as _useRealAuth,
  useRealAI as _useRealAI,
  shouldShowSampleData,
} from './app-mode';

export const mode = {
  /** Whether to call real Supabase edge functions vs return mock data */
  get useRealAPI(): boolean { return isBackendEnabled(); },

  /** Whether to use real Supabase auth vs simulated auth */
  get useRealAuth(): boolean { return _useRealAuth(); },

  /** Whether to use real AI models vs mock responses */
  get useRealAI(): boolean { return _useRealAI(); },

  /** Whether to show sample/demo data in the UI (mock mode, demo mode, or demo bypass) */
  get showSampleData(): boolean { return shouldShowSampleData(); },

  /** Whether the app is running with mock/offline data */
  get isMock(): boolean { return isMockMode(); },

  /** Whether this is a production environment */
  get isProduction(): boolean { return isProductionMode(); },

  /** Whether to enable analytics and error tracking */
  get enableTelemetry(): boolean { return isProductionMode(); },

  /** Whether to show developer/debug UI elements */
  get showDevTools(): boolean { return !isProductionMode(); },
} as const;
